import { Helmet } from 'react-helmet-async';
import { Box, Container, Drawer, Link, Stack, Typography } from '@mui/material';
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { Roadmap, RoadmapNode } from "../api/roadmap-service";
import { RoadmapService } from "../api/roadmap-service";
import type { Course } from "../api/course-service";
import CourseService, { coverUrl } from "../api/course-service";
import { UserProgressService } from "../api/user-progress-service";
import type { LessonStatuses } from "../utils/roadmap-progress";
import { courseIdsOf, stationProgress } from "../utils/roadmap-progress";
import ENV from "../env";
import Markdown from "../components/markdown/Markdown";
import { RoadmapBeltPicker } from "../components/roadmaps/RoadmapBeltPicker";
import { RoadmapLane } from "../components/roadmaps/RoadmapLane";
import { RoadmapLegend } from "../components/roadmaps/RoadmapLegend";
import { RoadmapNotesDialog } from "../components/roadmaps/RoadmapNotesDialog";
import { RoadmapStationPanel } from "../components/roadmaps/RoadmapStationPanel";
import { countNodes, treeAtLevel } from "../utils/roadmap-tree";

const LEVEL_PARAM = 'level';

/**
 * One roadmap as lanes of stations, judged against the belt the user aims for. The belt lives in the URL, so a link
 * shares the view; without one the highest belt shows the whole roadmap.
 */
export function RoadmapPage() {
    const [ roadmap, setRoadmap ] = useState<Roadmap | null>(null);
    const [ courses, setCourses ] = useState<Course[]>([]);
    const [ statuses, setStatuses ] = useState<LessonStatuses>(new Map());
    const [ station, setStation ] = useState<RoadmapNode | undefined>(undefined);
    const [ notesOf, setNotesOf ] = useState<RoadmapNode | undefined>(undefined);

    const { id } = useParams();
    const navigate = useNavigate();
    const [ searchParams, setSearchParams ] = useSearchParams();

    const maxLevel = roadmap?.levels[roadmap.levels.length - 1]?.level ?? 1;
    const level = Number(searchParams.get(LEVEL_PARAM)) || maxLevel;
    const tree = roadmap ? treeAtLevel(roadmap.root, level) : undefined;

    useEffect(() => {
        if (!id) return;

        RoadmapService.roadmap(id)
            .then(setRoadmap)
            .catch((reason) => {
                console.error("Unable to load roadmap: ", reason);
                navigate("/404");
            });
    }, [ id, navigate ]);

    useEffect(() => {
        CourseService.courses().then(setCourses).catch((reason) => console.error("Unable to load courses: ", reason));
    }, []);

    // Progress is kept per course, so the roadmap gathers it from every course its stations draw lessons from
    useEffect(() => {
        if (!roadmap) return;

        Promise.all(courseIdsOf(roadmap.root).map((courseId) => UserProgressService.userProgress(courseId).catch(() => null)))
            .then((all) => setStatuses(new Map(all.flatMap((progress) => Object.entries(progress?.lessons ?? {})
                .map(([ lessonId, lesson ]) => [ lessonId, lesson.status ] as const)))));
    }, [ roadmap ]);

    function handleLevelChange(next: number) {
        setSearchParams({ [LEVEL_PARAM]: String(next) }, { replace: true });
    }

    function handleOpenCourse(node: RoadmapNode) {
        if (node.courseId) navigate(`/courses/${ node.courseId }`);
    }

    function levelName(value: number): string {
        return roadmap?.levels.find((candidate) => candidate.level === value)?.name ?? String(value);
    }

    function coverOf(courseId?: string): string | undefined {
        const course = courses.find((candidate) => candidate.id === courseId);
        return course ? coverUrl(course) : undefined;
    }

    const progress = tree ? stationProgress(tree, statuses) : undefined;

    return <>
        <Helmet>
            <title>{ roadmap?.title ?? 'Roadmap' } | { ENV.APP_NAME }</title>
        </Helmet>

        <Container maxWidth="xl">
            <Stack spacing={ 3 }>
                <Stack component="nav" aria-label="Breadcrumb" direction="row" spacing={ 1 } sx={ { fontSize: 13, color: '#4F5B67' } }>
                    <Link component={ RouterLink } to="/roadmaps" underline="hover">Roadmaps</Link>
                    <span aria-hidden="true">/</span>
                    <span>{ roadmap?.title }</span>
                </Stack>

                <Stack direction={ { xs: 'column', md: 'row' } } justifyContent="space-between" alignItems={ { md: 'flex-end' } } spacing={ 4 }>
                    <Stack spacing={ 1 } sx={ { maxWidth: 680 } }>
                        <Typography component="h1" variant="inherit" sx={ { fontSize: 34, lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.02em' } }>
                            { roadmap?.title }
                        </Typography>
                        { roadmap?.description && (
                            <Box sx={ { fontSize: 15, lineHeight: '23px', color: '#4F5B67', '& p': { m: 0 } } }>
                                <Markdown text={ roadmap.description }/>
                            </Box>
                        ) }
                    </Stack>

                    { roadmap && tree && progress && (
                        <Stack direction="row" spacing={ 1.5 }>
                            <Stat value={ countNodes(tree, 'LESSON') } label={ `lessons at ${ levelName(level) }` }/>
                            <Stat value={ progress.finished } label="finished" color="#08660D"/>
                        </Stack>
                    ) }
                </Stack>

                { roadmap && <RoadmapBeltPicker levels={ roadmap.levels } value={ level } onChange={ handleLevelChange }/> }

                { roadmap && (
                    <Stack component="section" aria-label="Roadmap" spacing={ 1.75 } data-testid="roadmap-tree">
                        { roadmap.root.children.map((lane, index) => (
                            <RoadmapLane key={ lane.id ?? index } lane={ lane } index={ index } level={ level } statuses={ statuses }
                                         levelName={ levelName } coverOf={ coverOf } onOpen={ setStation }/>
                        )) }
                    </Stack>
                ) }

                <RoadmapLegend/>
            </Stack>
        </Container>

        <Drawer anchor="right" open={ !!station } onClose={ () => setStation(undefined) }
                PaperProps={ { sx: { width: { xs: '100%', sm: 560 } } } }>
            { station && (
                <RoadmapStationPanel key={ station.id ?? station.title } station={ station } level={ level } levelName={ levelName }
                                     onOpenCourse={ handleOpenCourse } onNotes={ setNotesOf } onClose={ () => setStation(undefined) }/>
            ) }
        </Drawer>

        <RoadmapNotesDialog node={ notesOf } onClose={ () => setNotesOf(undefined) }/>
    </>;
}

type StatProps = {
    value: number;
    label: string;
    color?: string;
};

function Stat({ value, label, color }: StatProps) {
    return (
        <Stack spacing={ 0.25 } sx={ { bgcolor: '#FFFFFF', border: '1px solid #E3E8EE', borderRadius: '14px', px: 2, py: 1.5, minWidth: 120 } }>
            <Box component="span" sx={ { fontSize: 24, fontWeight: 800, color } }>{ value }</Box>
            <Box component="span" sx={ { fontSize: 12, color: '#4F5B67' } }>{ label }</Box>
        </Stack>
    );
}
